import { AvailableAccount } from "../accounts/AvailableAccount";
import { FEATURE_TEMPO_KEY } from "../features.constants";

export type AccountPrerequisiteId =
  | "account-category"
  | "account-app"
  | "tempo-facility"
  | "tempo-template"
  | "tempo-mobile-header"
  | "tempo-mobile-background"
  | "catalog-profile-field";

export interface AccountPrerequisiteDefinition {
  /**
   * Stable identifier used in code and caching.
   */
  id: AccountPrerequisiteId;

  /**
   * Human readable label shown to the user in the guard dialog.
   */
  label: string;

  /**
   * Optional longer description of why this prerequisite matters.
   */
  description?: string;

  /**
   * Optional feature key this prerequisite depends on (e.g. Tempo).
   * If the feature is not enabled for the current account, the prerequisite
   * is considered satisfied / not applicable.
   */
  dependsOnFeatureKey?: string;

  /**
   * Factory that builds the recommended router link for satisfying this
   * prerequisite for the given account.
   */
  recommendedRouteFactory: (account: AvailableAccount) => any[];
}

export interface AccountPrerequisiteInstance
  extends AccountPrerequisiteDefinition {
  /**
   * Resolved router link for the current account.
   */
  recommendedRoute: any[];
}

/**
 * Static list of prerequisite definitions.
 *
 * NOTE: Route factories deliberately keep things simple and point to
 * existing, stable setup areas:
 * - Account setup screen for account-level config
 * - Apps list for account apps
 * - Content → Template Library and Media Library for Tempo assets
 * - Account Catalog editor for custom profile fields
 */
export const ACCOUNT_PREREQUISITES: AccountPrerequisiteDefinition[] = [
  {
    id: "account-category",
    label: "At least one Account Category",
    description:
      "Create at least one account category so you can target and organize content properly.",
    recommendedRouteFactory: (account: AvailableAccount) => [
      "accounts",
      account.accountID.toString(),
    ],
  },
  {
    id: "account-app",
    label: "At least one Account App",
    description:
      "Add at least one app so you can deliver content to end users.",
    recommendedRouteFactory: () => ["apps", "list"],
  },
  {
    id: "tempo-facility",
    label: "At least one Facility (Tempo)",
    description:
      "Create at least one facility so Tempo experiences can be associated with real locations.",
    dependsOnFeatureKey: FEATURE_TEMPO_KEY,
    recommendedRouteFactory: () => ["facilities", "list"],
  },
  {
    id: "tempo-mobile-header",
    label: "At least one Mobile Header image (Tempo)",
    description:
      "Upload a mobile header image for your Tempo experiences in the media library.",
    dependsOnFeatureKey: FEATURE_TEMPO_KEY,
    recommendedRouteFactory: () => ["content", "media", "library"],
  },
  {
    id: "tempo-mobile-background",
    label: "At least one Mobile Background image (Tempo)",
    description:
      "Upload a mobile background image for your Tempo experiences in the media library.",
    dependsOnFeatureKey: FEATURE_TEMPO_KEY,
    recommendedRouteFactory: () => ["content", "media", "library"],
  },
  {
    id: "tempo-template",
    label: "At least one Template (Tempo)",
    description:
      "Create at least one Tempo template so you can send mobile experiences.",
    dependsOnFeatureKey: FEATURE_TEMPO_KEY,
    recommendedRouteFactory: () => ["content", "template", "library"],
  },
  {
    id: "catalog-profile-field",
    label: "At least one custom profile field in the account catalog",
    description:
      "Add at least one custom profile field so you can segment and personalize communications.",
    recommendedRouteFactory: (account: AvailableAccount) => [
      "accounts",
      account.accountID.toString(),
      "catalog",
    ],
  },
];


